import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Code, Layers, Database, Smartphone, Palette, Music, ChevronRight, ExternalLink, Award, Briefcase } from 'lucide-react';

export default function Portfolio() {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('hero');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const projects = [
    {
      title: "Athelink",
      description: "Sports networking platform connecting athletes, coaches, and organizers with real-time updates on trials, selections, and events. Features personalized feeds and comprehensive user engagement.",
      tech: ["React", "Node.js", "MongoDB", "REST APIs"],
      highlight: "Full-Stack Platform"
    },
    {
      title: "Jenuvan AI",
      description: "AI-powered tutoring Chrome extension that learns from online classes, analyzes content, and provides personalized concept explanations mimicking a human tutor.",
      tech: ["JavaScript", "AI/ML", "Chrome Extension"],
      highlight: "AI Innovation"
    },
    {
      title: "Student ERP System",
      description: "Comprehensive college ERP application with attendance, marks management, analysis modules, and secure staff-student interaction for efficient academic data handling.",
      tech: ["React", "Node.js", "Database Management"],
      highlight: "Enterprise Solution"
    },
    {
      title: "Quest Beyond the Wall",
      description: "Game of Thrones-themed treasure hunt website with digitized gameplay, automated clue progression, and interactive engagement interface.",
      tech: ["Web Development", "Interactive UI"],
      highlight: "Gamification"
    },
    {
      title: "Nether Quest",
      description: "Minecraft-themed online treasure hunt platform featuring gamified participation, automated progression, and immersive visual interface.",
      tech: ["Web Development", "Game Design"],
      highlight: "Interactive Gaming"
    },
    {
      title: "Engenia",
      description: "Cultural event management website with real-time leaderboards, event updates, and winner details for seamless fest coordination.",
      tech: ["Real-time Updates", "Event Management"],
      highlight: "Live Dashboard"
    }
  ];

  const skills = [
    { icon: <Code className="w-6 h-6" />, name: "Languages", items: ["JavaScript", "Python"] },
    { icon: <Layers className="w-6 h-6" />, name: "Frontend", items: ["React", "UI/UX Design", "Figma"] },
    { icon: <Database className="w-6 h-6" />, name: "Backend", items: ["Node.js", "Express.js", "MongoDB", "Supabase"] },
    { icon: <Smartphone className="w-6 h-6" />, name: "Other Tech", items: ["REST APIs", "Web3", "Smart Contracts", "Android Dev"] }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-green-950 to-black"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(34, 197, 94, 0.15) 0%, transparent 50%)`,
          }}
        ></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(34, 197, 94, 0.03) 2px, rgba(34, 197, 94, 0.03) 4px)`,
        }}></div>
      </div>

      {/* Floating Particles */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-green-500 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          ></div>
        ))}
      </div>

      {/* Floating Sidebar */}
      <nav className="fixed left-8 top-1/2 transform -translate-y-1/2 z-50 hidden lg:block">
        <div className="bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-2xl p-4 shadow-2xl shadow-green-500/20">
          {['hero', 'about', 'skills', 'projects', 'contact'].map((section, idx) => (
            <button
              key={section}
              onClick={() => document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' })}
              className="block mb-4 last:mb-0 group relative"
            >
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeSection === section 
                  ? 'bg-green-500 scale-150' 
                  : 'bg-green-500/30 hover:bg-green-500/60'
              }`}></div>
              <span className="absolute left-8 top-1/2 -translate-y-1/2 bg-green-500 text-black px-3 py-1 rounded-lg text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section id="hero" className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div 
              className="transform transition-all duration-1000"
              style={{ transform: `translateY(${scrollY * 0.3}px)` }}
            >
              <div className="inline-block mb-6 px-6 py-2 bg-green-500/10 border border-green-500/30 rounded-full backdrop-blur-sm animate-pulse">
                <span className="text-green-400 text-sm font-semibold">Available for Opportunities</span>
              </div>
              
              <h1 className="text-7xl md:text-9xl font-bold mb-6 bg-gradient-to-r from-green-400 via-green-500 to-emerald-400 bg-clip-text text-transparent animate-pulse">
                JARON
              </h1>
              
              <p className="text-2xl md:text-4xl text-green-300 mb-4 font-light">
                Full-Stack Developer & Web3 Enthusiast
              </p>
              
              <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto mb-12 leading-relaxed">
                Crafting innovative digital experiences with cutting-edge technologies. 
                Specializing in scalable web applications and decentralized solutions.
              </p>

              <div className="flex gap-6 justify-center mb-12">
                <a href="#contact" className="group relative px-8 py-4 bg-green-500 text-black font-bold rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/50">
                  <span className="relative z-10">Get In Touch</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                </a>
                <a href="#projects" className="px-8 py-4 border-2 border-green-500 text-green-400 font-bold rounded-xl hover:bg-green-500/10 transition-all duration-300 hover:scale-105">
                  View Projects
                </a>
              </div>

              <div className="flex gap-6 justify-center">
                <a href="https://github.com/Jaroncoder" className="w-12 h-12 flex items-center justify-center bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 hover:scale-110 transition-all duration-300">
                  <Github className="w-6 h-6 text-green-400" />
                </a>
                <a href="https://www.linkedin.com/in/jaron-ce-97b941295?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" className="w-12 h-12 flex items-center justify-center bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 hover:scale-110 transition-all duration-300">
                  <Linkedin className="w-6 h-6 text-green-400" />
                </a>
                <a href="mailto:jaronce2005@gmail.com" className="w-12 h-12 flex items-center justify-center bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 hover:scale-110 transition-all duration-300">
                  <Mail className="w-6 h-6 text-green-400" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-12 text-center bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              About Me
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-8 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20 transform hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center gap-3">
                  <Award className="w-8 h-8" />
                  Education
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">B.E in Computer Science Engineering</h4>
                    <p className="text-green-300">Loyola ICAM College of Engineering & Technology</p>
                    <p className="text-gray-400">CGPA: 8.07 (Ongoing)</p>
                  </div>
                  <div className="border-t border-green-500/20 pt-4">
                    <h4 className="text-lg font-semibold text-white mb-2">Matriculation</h4>
                    <p className="text-green-300">Campion Anglo-Indian Higher Secondary School</p>
                    <p className="text-gray-400">80.83% • Cutoff: 167.5</p>
                  </div>
                </div>
              </div>

              <div className="bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-8 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20 transform hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center gap-3">
                  <Briefcase className="w-8 h-8" />
                  Experience & Leadership
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Cultural Coordinator</h4>
                    <p className="text-green-300">CSE Department - ENGENIA 2025</p>
                    <p className="text-gray-400 mt-2">Leading cultural initiatives and coordinating department-wide events for Loyola ICAM's premier cultural fest.</p>
                  </div>
                  <div className="border-t border-green-500/20 pt-4">
                    <h4 className="text-lg font-semibold text-white mb-2">Music Programming</h4>
                    <p className="text-gray-400">Blending technical skills with creative artistry</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-8 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20">
              <p className="text-gray-300 text-lg leading-relaxed">
                Results-driven Computer Science student with a strong foundation in <span className="text-green-400 font-semibold">full-stack web development</span> using React, Node.js, Express.js, and MongoDB. Excelled in building efficient, scalable, and user-centric web applications. Currently exploring <span className="text-green-400 font-semibold">Web3 and blockchain technologies</span> to expand expertise into decentralized systems and smart contract development. Committed to continuous learning, innovation, and delivering impactful digital solutions.
              </p>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-6xl mx-auto w-full">
            <h2 className="text-5xl md:text-6xl font-bold mb-12 text-center bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Skills & Expertise
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {skills.map((category, idx) => (
                <div
                  key={idx}
                  className="group bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-6 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20 transform hover:-translate-y-3"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-green-500/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors duration-300 group-hover:scale-110 transform">
                    <div className="text-green-400 group-hover:rotate-12 transition-transform duration-300">
                      {category.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{category.name}</h3>
                  <div className="space-y-2">
                    {category.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-green-500" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-7xl mx-auto w-full">
            <h2 className="text-5xl md:text-6xl font-bold mb-12 text-center bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Featured Projects
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, idx) => (
                <div
                  key={idx}
                  className="group relative bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-6 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20 transform hover:-translate-y-3 overflow-hidden"
                >
                  <div className="absolute top-4 right-4 px-3 py-1 bg-green-500 text-black text-xs font-bold rounded-full">
                    {project.highlight}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3 mt-6 group-hover:text-green-400 transition-colors">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  
                  <button className="flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold group-hover:gap-3 transition-all">
                    View Details
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-4xl mx-auto w-full text-center">
            <h2 className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Let's Build Something Amazing
            </h2>
            
            <p className="text-gray-400 text-xl mb-12 leading-relaxed">
              I'm always excited to collaborate on innovative projects and explore new opportunities. 
              Whether you have a project in mind or just want to connect, feel free to reach out!
            </p>

            <div className="bg-black/40 backdrop-blur-lg border border-green-500/30 rounded-3xl p-8 hover:border-green-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/20">
              <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
                <a href="mailto:jaronce2005@gmail.com" className="flex items-center gap-3 px-8 py-4 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-green-500/50">
                  <Mail className="w-5 h-5" />
                  Email Me
                </a>
                <a href="https://www.linkedin.com/in/jaron-ce-97b941295?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" className="flex items-center gap-3 px-8 py-4 border-2 border-green-500 text-green-400 font-bold rounded-xl hover:bg-green-500/10 transition-all duration-300 hover:scale-105">
                  <Linkedin className="w-5 h-5" />
                  Connect on LinkedIn
                </a>
                <a href="https://github.com/Jaroncoder" className="flex items-center gap-3 px-8 py-4 border-2 border-green-500 text-green-400 font-bold rounded-xl hover:bg-green-500/10 transition-all duration-300 hover:scale-105">
                  <Github className="w-5 h-5" />
                  View GitHub
                </a>
              </div>
            </div>

            <div className="mt-12 text-gray-500">
              <p>&copy; 2025 Jaron. Crafted with passion and precision.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
